import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, BadgeCheck } from "lucide-react";
import { errMsg, fmtDate, openBillingPortal, startCheckout } from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";

const RETURN_MSG = {
  success: { cls: "bg-emerald-50 text-emerald-900 border-emerald-200", text: "Thank you! Your MileageTax Monthly checkout was completed." },
  cancelled: { cls: "bg-slate-50 text-slate-700 border-slate-200", text: "Checkout was cancelled. You have not been charged." },
};

const ManageButton = () => {
  const [loading, setLoading] = useState(false);
  const manage = async () => {
    setLoading(true);
    try {
      await openBillingPortal();
    } catch (err) {
      toast.error(errMsg(err));
      setLoading(false);
    }
  };
  return (
    <button type="button" onClick={manage} disabled={loading} className="mt-btn-outline !h-11 w-full sm:w-auto mt-3" data-testid="manage-subscription-button">
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      Manage Subscription
    </button>
  );
};

const SubscribedBadge = ({ sub }) => (
  <div className="text-left sm:text-right" data-testid="subscription-active">
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 font-bold text-sm" data-testid="subscription-status-badge">
      <BadgeCheck className="w-4 h-4" /> Subscribed
    </span>
    {sub.current_period_end && (
      <p className="text-xs text-slate-500 mt-2" data-testid="subscription-renewal">
        {sub.cancel_at_period_end ? "Cancels" : "Renews"} on {fmtDate(sub.current_period_end)}
      </p>
    )}
    <ManageButton />
  </div>
);

export const SubscriptionCard = ({ icon: Icon }) => {
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const { sub } = useSubscription();
  const checkout = params.get("checkout");
  const status = RETURN_MSG[checkout];

  useEffect(() => {
    if (checkout === "success") toast.success("Checkout completed");
  }, [checkout]);

  const subscribe = async () => {
    setLoading(true);
    try {
      await startCheckout();
    } catch (err) {
      toast.error(errMsg(err));
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="subscription-section">
      {status && (
        <p className={`text-sm font-semibold rounded-lg border px-3 py-2 ${status.cls}`} data-testid={`checkout-${checkout}-message`}>
          {status.text}
        </p>
      )}
      <div className="rounded-xl border border-slate-200 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-emerald-700" />
          </span>
          <div>
            <p className="font-heading text-lg font-bold text-slate-900" data-testid="subscription-plan-name">MileageTax Monthly</p>
            <p className="text-slate-600" data-testid="subscription-plan-price">
              <span className="font-mono font-bold text-slate-900">$9.99</span>/month
            </p>
          </div>
        </div>
        {sub === null ? (
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" data-testid="subscription-loading" />
        ) : sub.subscribed ? (
          <SubscribedBadge sub={sub} />
        ) : (
          <button type="button" onClick={subscribe} disabled={loading} className="mt-btn-primary !h-12 w-full sm:w-auto" data-testid="subscribe-button">
            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
            Subscribe
          </button>
        )}
      </div>
      {sub && !sub.subscribed && (
        <p className="text-xs text-slate-500">You'll be taken to Stripe's secure checkout page to complete your subscription.</p>
      )}
    </div>
  );
};
